import { api } from '@goaltree/backend/convex/_generated/api'
import * as Sentry from '@sentry/react'
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState } from 'react'

import { ReviewPromptHost } from '@/components/app-review'
import { ScrollFab } from '@/components/goal-tree-fab'
import Loader from '@/components/loader'
import { useAccentTheme } from '@/hooks/use-accent-theme'
import { isNewUser, takePendingAuthMethod } from '@/lib/auth-analytics'
import { authClient } from '@/lib/auth-client'
import { todayKey } from '@/lib/day'
import { getFirstTouch } from '@/lib/first-touch'
import { useViewStore } from '@/store/view.store'

const localDateStr = todayKey

// Warms Convex subscriptions at the root so data is already streaming
// when the user navigates to any tab.

// Screens without their own FAB that still get the shared new task/habit FAB
// (Today, Everyday and Goals mount theirs; chat, settings and flows don't).
// Explore and Leaderboard skip it: it covered their list CTAs.
const FAB_SCREENS = /^\/(stats|notifications|templates\/?$|habits\/)/

function QueryPreloader() {
  const localDate = localDateStr()
  useQuery(api.nodes.getUserGoals)
  useQuery(api.nodes.getGoalsFullTree)
  useQuery(api.dailySurface.getTodayWithContext, { localDate })
  useQuery(api.preferences.getMyPreferences)
  useQuery(api.preferences.getMyHandle)
  useQuery(api.preferences.getMyBio)
  useQuery(api.auth.getCurrentUser)
  useQuery(api.nodes.getUserStreak)
  useQuery(api.nodes.getUserStreakInfo)
  useQuery(api.subscriptions.getMySubscription)
  useQuery(api.leaderboard.getGlobalLeaderboard)
  useQuery(api.leaderboard.getFriendsLeaderboard)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useQuery((api as any).follows.getMyFollowingIds)
  return null
}

import { useRouterState } from '@tanstack/react-router'

import { BadgeCelebration } from '@/components/badge-celebration'
import { BottomNav } from '@/components/bottom-nav'
import { CelebrationLayer } from '@/components/celebration-layer'
import { FinishCelebration } from '@/components/dot-finishes'
import { PhotoCheckinHost } from '@/components/dot-photo'
import { EventSheetHost } from '@/components/event-detail-sheet'
import { HabitChainWatcher } from '@/components/habit-stack'
import { IncomingCoachCall } from '@/components/incoming-coach-call'
import { InstallSheet } from '@/components/install-sheet'
import { MustDoFullDialog } from '@/components/must-do'
import { TimerBell } from '@/components/number-habit'
import { OfflineBanner } from '@/components/offline-banner'
import { QuickCapture } from '@/components/quick-capture'
import { ShareCardHost } from '@/components/share-card'
import { SoftUpsell } from '@/components/soft-upsell'
import { useOfflineReplay } from '@/hooks/use-offline-mutations'
import { useWebPush } from '@/hooks/use-web-push'
import { clearReferralCode, readReferralCode } from '@/lib/referral'
import { takeReturnTo } from '@/lib/return-to'
import {
  clearSessionIndicator,
  hasSessionIndicator,
  setSessionIndicator,
} from '@/lib/session-indicator'
import { useOnboardingStore } from '@/store/onboarding.store'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedLayout,
})

function PushAutoSubscribe() {
  useWebPush()
  return null
}

function AuthenticatedLayout() {
  const { isAuthenticated, isLoading } = useConvexAuth()
  useOfflineReplay(isAuthenticated)
  const navigate = useNavigate()
  const posthog = usePostHog()
  const currentUser = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const prefs = useQuery(api.preferences.getMyPreferences, isAuthenticated ? {} : 'skip')
  const goals = useQuery(api.nodes.getUserGoals, isAuthenticated ? {} : 'skip')
  const hasBeenAuthenticated = useRef(false)
  // Set right before a Google redirect. When we land here straight from OAuth,
  // Convex hasn't minted a token yet (transient 401) — wait it out instead of
  // bouncing to /auth/login.
  const [oauthPending, setOauthPending] = useState(
    () => typeof window !== 'undefined' && sessionStorage.getItem('oauthPending') === '1',
  )
  const { location } = useRouterState()
  const updateTimezone = useMutation(api.preferences.updateTimezone)
  const syncEmail = useMutation(api.preferences.syncEmail)
  const redeemReferral = useMutation(api.referrals.redeemReferral)
  useEffect(() => {
    if (!isAuthenticated) return
    void syncEmail({})
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Report the local timezone only while this tab is actively in the foreground,
  // so the device the user is actually using is always the most-recent writer.
  // The backend schedules daily notifications (e.g. the morning briefing) off
  // this single per-user offset — a stale background tab in another timezone
  // (a desktop left open abroad) must not clobber it and push the phone's
  // briefing to the wrong hour. On focus/visibility we re-assert so switching
  // devices hands the timezone to whichever one is in use.
  useEffect(() => {
    if (!isAuthenticated) return
    const report = () => {
      if (document.visibilityState !== 'visible') return
      void updateTimezone({ tzOffsetMinutes: new Date().getTimezoneOffset() })
    }
    report()
    window.addEventListener('focus', report)
    document.addEventListener('visibilitychange', report)
    return () => {
      window.removeEventListener('focus', report)
      document.removeEventListener('visibilitychange', report)
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // The public day page (gt.today/<handle>) tints itself with the app accent,
  // which otherwise lives only in localStorage.
  const { accent, rotate } = useAccentTheme()
  const accentPref = rotate ? 'rotate' : accent
  const savedAccent = prefs && 'accent' in prefs ? prefs.accent : undefined
  const updatePreferences = useMutation(api.preferences.updatePreferences)
  useEffect(() => {
    if (!isAuthenticated || prefs === undefined || savedAccent === accentPref) return
    void updatePreferences({ accent: accentPref }).catch(() => {})
  }, [isAuthenticated, prefs === undefined, savedAccent, accentPref]) // eslint-disable-line react-hooks/exhaustive-deps

  // Redeem a referral handed off from the marketing site (gt_ref cookie) once
  // signed in. Server-side guards make it a no-op for non-new / ineligible users;
  // clear the cookie either way so it only ever fires once.
  const referralRedeemed = useRef(false)
  useEffect(() => {
    if (!isAuthenticated || referralRedeemed.current) return
    const code = readReferralCode()
    if (!code) return
    referralRedeemed.current = true
    void redeemReferral({ code }).finally(() => clearReferralCode())
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated) hasBeenAuthenticated.current = true
  }, [isAuthenticated])

  // An invite opened while signed out: auth paths that drop ?returnTo= land here.
  useEffect(() => {
    if (!isAuthenticated) return
    const to = takeReturnTo()
    if (to) navigate({ to, replace: true })
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the OAuth-pending marker once auth resolves, or after a grace period
  // if it never does (cancelled / failed) so we don't wait forever.
  useEffect(() => {
    if (isAuthenticated) {
      sessionStorage.removeItem('oauthPending')
      setOauthPending(false)
    }
  }, [isAuthenticated])
  useEffect(() => {
    if (!oauthPending) return
    const timer = setTimeout(() => {
      sessionStorage.removeItem('oauthPending')
      setOauthPending(false)
    }, 12_000)
    return () => clearTimeout(timer)
  }, [oauthPending])

  // Cold-resume recovery. We disabled refetch-on-window-focus globally (it
  // flickers Convex auth for logged-in users), so when the PWA is reopened after
  // a long sleep the expired Convex token isn't refreshed and useConvexAuth
  // momentarily reports unauthenticated — landing the user on /auth/login until
  // they manually reload. When we resolve to unauthenticated while online but
  // still holding a session indicator (a real prior login on this device), it's
  // almost always that refresh race: force one session refetch to mint a fresh
  // token and hold a spinner during a grace window instead of bouncing to login.
  // Gated on !isAuthenticated, so logged-in users never get the focus flicker.
  const [resumeGraceElapsed, setResumeGraceElapsed] = useState(false)
  const inResumeGrace =
    !isLoading &&
    !isAuthenticated &&
    !oauthPending &&
    navigator.onLine &&
    hasSessionIndicator() &&
    !resumeGraceElapsed
  useEffect(() => {
    if (!inResumeGrace) return
    let cancelled = false
    void (async () => {
      const res = await authClient
        .getSession({ query: { disableCookieCache: true } })
        .catch(() => null)
      if (cancelled) return
      // Session exists on the server — the provider will flip isAuthenticated.
      if (res?.data) return
      // No in-place session but we hold a session indicator: the auth client is
      // stale (its refetch didn't recover). A full reload re-bootstraps auth from
      // storage — exactly what a manual refresh does — so we recover instead of
      // showing the login screen. Reload once; if it's still gone afterward the
      // session is genuinely over and we fall through to /auth/login.
      if (sessionStorage.getItem('authReloadTried') !== '1') {
        sessionStorage.setItem('authReloadTried', '1')
        window.location.reload()
      } else {
        setResumeGraceElapsed(true)
      }
    })()
    // Fallback if getSession hangs.
    const timer = setTimeout(() => setResumeGraceElapsed(true), 8_000)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [inResumeGrace])
  useEffect(() => {
    if (isAuthenticated) {
      setResumeGraceElapsed(false)
      sessionStorage.removeItem('authReloadTried')
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isLoading || isAuthenticated || oauthPending) return
    // Don't redirect if the browser is offline and the session cookie exists.
    // Convex reports isAuthenticated:false when it can't reach the server, but
    // the session is still valid. The cookie survives page reloads; when
    // connectivity restores, Convex re-establishes auth automatically.
    if (!navigator.onLine && hasSessionIndicator()) return
    // Still waiting out the cold-resume recovery (refetch + one reload) — don't
    // bounce to login.
    if (inResumeGrace) return
    sessionStorage.removeItem('authReloadTried')
    clearSessionIndicator()
    // gt.today/ sends everyone here; visitors without an account belong on the marketing site.
    if (
      location.pathname === '/today' &&
      new URLSearchParams(window.location.search).get('src') === 'gt'
    ) {
      window.location.replace('https://goaltree.ai/?utm_source=gt.today')
      return
    }
    // Old "Get started" links pointed at onboarding; signed-out, that's the planner.
    navigate({
      to: location.pathname.startsWith('/onboarding') ? '/goals/new' : '/auth/login',
      replace: true,
    })
  }, [isAuthenticated, isLoading, oauthPending, inResumeGrace, navigate])

  useEffect(() => {
    if (isAuthenticated) setSessionIndicator()
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated || !currentUser) return
    const id = currentUser._id as string
    const email = currentUser.email ?? undefined
    const name = currentUser.name ?? undefined
    Sentry.setUser({ id, email, username: name })
    posthog?.identify(id, { email, name })
    const method = takePendingAuthMethod()
    if (method) {
      const u = currentUser as { createdAt?: number; _creationTime?: number }
      posthog?.capture('auth_completed', {
        method,
        isNewUser: isNewUser(u.createdAt ?? u._creationTime),
      })
    }
  }, [isAuthenticated, currentUser, posthog])

  // Signup attribution: the first-touch cookie goes on the user record once, with
  // one signed_up event, whichever way they signed up (email, redirect, One Tap).
  const saveAcquisition = useMutation(api.preferences.saveAcquisition)
  const acquisitionTried = useRef(false)
  useEffect(() => {
    if (acquisitionTried.current || !currentUser || prefs === undefined) return
    const u = currentUser as { createdAt?: number; _creationTime?: number }
    const created = u.createdAt ?? u._creationTime ?? 0
    acquisitionTried.current = true
    if ((prefs as { acquisition?: unknown }).acquisition || Date.now() - created > 86_400_000)
      return
    const ft = getFirstTouch() ?? { timestamp: Date.now() }
    void saveAcquisition(ft)
      .then((saved) => {
        if (!saved) return
        const props = {
          first_referrer: ft.referrer ?? null,
          first_utm_source: ft.utmSource ?? null,
          first_utm_medium: ft.utmMedium ?? null,
          first_utm_campaign: ft.utmCampaign ?? null,
          first_landing_url: ft.landingUrl ?? null,
          first_gclid: ft.gclid ?? null,
          first_fbclid: ft.fbclid ?? null,
        }
        posthog?.capture('signed_up', { ...props, $set_once: props })
      })
      .catch(() => {})
  }, [currentUser, prefs, saveAcquisition, posthog])

  useEffect(() => {
    if (!isAuthenticated) {
      Sentry.setUser(null)
      posthog?.reset()
    }
  }, [isAuthenticated, posthog])

  // Hydrate the day-start hour so client-side day/streak calcs match the server.
  const setDayStartHour = useViewStore((s) => s.setDayStartHour)
  useEffect(() => {
    const h = (prefs as { dayStartHour?: number } | undefined)?.dayStartHour
    if (typeof h === 'number') setDayStartHour(h)
  }, [prefs, setDayStartHour])

  useEffect(() => {
    if (!isAuthenticated || prefs === undefined || goals === undefined) return
    if (location.pathname.startsWith('/onboarding')) return
    // Account deletion empties goals/prefs while still briefly authenticated —
    // don't mistake that for a new user needing onboarding.
    if (sessionStorage.getItem('accountDeleting') === '1') return
    const realGoals = goals.filter((g) => !g.isInbox)
    if (!(prefs as Record<string, unknown>).onboardingCompletedAt && realGoals.length === 0) {
      // Habits-only newcomers have no goal to make; resume their habit steps instead.
      const focus = useOnboardingStore.getState().focus ?? (prefs as { focus?: string }).focus
      navigate({
        to: focus === 'habits' ? '/onboarding/user-profile' : '/goals/new',
        replace: true,
      })
    }
  }, [isAuthenticated, prefs, goals, location.pathname])

  // Hold the spinner until we can decide whether the user needs onboarding, so a
  // new/re-signed-up user never flashes the app before the redirect. Compute the
  // decision synchronously here (not just in the effect) — otherwise the render
  // that releases the gate shows the app for one frame before the effect navigates.
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const onOnboarding = location.pathname.startsWith('/onboarding')
  const undecided =
    isAuthenticated &&
    !onOnboarding &&
    (prefs === undefined ||
      (!(prefs as Record<string, unknown>).onboardingCompletedAt && goals === undefined))
  const needsOnboarding =
    isAuthenticated &&
    !onOnboarding &&
    prefs !== undefined &&
    goals !== undefined &&
    !(prefs as Record<string, unknown>).onboardingCompletedAt &&
    goals.filter((g) => !g.isInbox).length === 0 &&
    sessionStorage.getItem('accountDeleting') !== '1'

  if (
    isLoading ||
    (oauthPending && !isAuthenticated) ||
    inResumeGrace ||
    undecided ||
    needsOnboarding
  ) {
    return <Loader />
  }

  // Stay mounted when offline with a valid session cookie — network drop shouldn't blank the screen
  if (!isAuthenticated && (navigator.onLine || !hasSessionIndicator())) return null

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-[60] pt-[env(safe-area-inset-top,0px)]">
        <OfflineBanner />
      </div>
      <QueryPreloader />
      <HabitChainWatcher />
      <TimerBell />
      <PushAutoSubscribe />
      <Outlet />
      <BottomNav />
      {FAB_SCREENS.test(pathname) && <ScrollFab />}
      <QuickCapture />
      <ReviewPromptHost />
      <InstallSheet />
      <BadgeCelebration />
      <FinishCelebration />
      <CelebrationLayer />
      <ShareCardHost />
      <PhotoCheckinHost />
      <EventSheetHost />
      <IncomingCoachCall />
      <SoftUpsell />
      <MustDoFullDialog />
    </>
  )
}
